import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { change_Current_Location, changeHeaderTitle, checkLogin, setOtpVerified } from '../../Common/store';
import { terminalLogout } from '../api/cp';
import AppLogoutConfirmModal from '../components/AppLogoutConfirmModal';
import { CreditCard, FileSearch, LogOut } from 'lucide-react';
import { getAppLogoAssetUrl, getAppHeaderLogoStyle } from '../../Common/constants/adminImages';

/**
 * 앱 대시보드 - 결제하기 / 조회하기 두 개의 큰 페이지 이동 버튼
 * 모바일 전체 화면 (maxWidth 768px, 100dvh)
 * hideHeader: true 시 헤더 숨김 (AppDashboardPage에서 통합 헤더 사용)
 */
function AppDashboard({ hideHeader = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  useEffect(() => {
    dispatch(change_Current_Location('대시보드'));
    dispatch(changeHeaderTitle({ mainTitle: '대시보드', subTitle: '' }));
  }, [dispatch]);

  const confirmLogout = async () => {
    try {
      setLogoutLoading(true);
      try {
        await terminalLogout();
      } catch (e) {}
      dispatch(setOtpVerified(false));
      dispatch(checkLogin(false));
      setLogoutModalOpen(false);
      navigate('/app');
    } finally {
      setLogoutLoading(false);
    }
  };

  return (
    <div
      style={{
        ...(hideHeader ? { flex: 1, minHeight: 0 } : { height: '100dvh', minHeight: '100dvh' }),
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '768px',
        margin: '0 auto',
        background: '#FFFFFF',
        overflow: 'hidden',
      }}
    >
      <style>
        {`
          button, a, [onClick] {
            outline: none !important;
            -webkit-tap-highlight-color: transparent;
            -webkit-touch-callout: none;
            user-select: none;
            transition: transform 0.1s ease, opacity 0.1s ease;
          }
          button:focus, a:focus, [onClick]:focus { outline: none !important; }
          button:active, a:active, [onClick]:active {
            outline: none !important;
            transform: scale(0.98);
            opacity: 0.9;
          }
        `}
      </style>

      {!hideHeader && (
        <>
          {/* 상단 헤더 - 왼쪽 로그아웃, 로고(가운데), 오른쪽 A,B 토글 영역(공간) */}
          <div
            style={{
              background: 'linear-gradient(160deg, #0b3a6f 0%, #0d2b53 60%, #08223f 100%)',
              height: '62px',
              padding: '0 12px 0 10px',
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 2px',
                width: '100%',
                position: 'relative',
              }}
            >
              <button
                type="button"
                onClick={() => setLogoutModalOpen(true)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FFFFFF',
                }}
              >
                <LogOut size={22} color="#FFFFFF" />
              </button>
              <img
                src={getAppLogoAssetUrl()}
                alt=""
                style={getAppHeaderLogoStyle({
                  width: '150px',
                  height: 'auto',
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  pointerEvents: 'none',
                })}
              />
              <div style={{ width: '60px', height: '30px' }} />
            </div>
          </div>
        </>
      )}

      {/* 본문 - 결제하기 / 조회하기 큰 버튼 */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'stretch',
          padding: '32px 24px',
          gap: '24px',
          boxSizing: 'border-box',
        }}
      >
        <button
          type="button"
          onClick={() => navigate('/app/payment', { state: { from: '/app/dashboard' } })}
          style={{
            width: '100%',
            minHeight: '120px',
            padding: '28px 24px',
            background: 'linear-gradient(135deg, #3182F6 0%, #2563EB 100%)',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '20px',
            fontSize: '22px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            boxShadow: '0 8px 24px rgba(49, 130, 246, 0.35)',
          }}
        >
          <CreditCard size={40} strokeWidth={2.5} />
          <span>수기결제</span>
          <span style={{ fontSize: '14px', fontWeight: 500, opacity: 0.9 }}>
            수기결제 화면으로 이동
          </span>
        </button>

        <button
          type="button"
          onClick={() => navigate('/app/payment-list')}
          style={{
            width: '100%',
            minHeight: '120px',
            padding: '28px 24px',
            background: '#6b7280',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '20px',
            fontSize: '22px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            boxShadow: '0 8px 24px rgba(107, 114, 128, 0.35)',
          }}
        >
          <FileSearch size={40} strokeWidth={2.5} />
          <span>결제내역</span>
          <span style={{ fontSize: '14px', fontWeight: 500, opacity: 0.9 }}>
            결제내역 화면으로 이동
          </span>
        </button>
      </div>

      <footer
        style={{
          flexShrink: 0,
          paddingTop: '0.5rem',
          paddingBottom: '0.5rem',
          textAlign: 'center',
          fontSize: '12px',
          color: '#8b95a1',
          borderTop: '1px solid #f2f4f6',
        }}
      >
        Copyright 2026 nToss-Pharm. All rights reserved.
      </footer>

      {!hideHeader && (
        <AppLogoutConfirmModal
          open={logoutModalOpen}
          onCancel={() => {
            if (!logoutLoading) setLogoutModalOpen(false);
          }}
          onConfirm={confirmLogout}
          loading={logoutLoading}
        />
      )}
    </div>
  );
}

export default AppDashboard;
